import { useState, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import * as api from '../../lib/api'
import { useToast } from '../../contexts/ToastContext'
import Modal from '../../components/Modal'
import CustomSelect from '../../components/CustomSelect/CustomSelect'
import { useTour } from '../../contexts/TourContext'

// ─── Helpers ───────────────────────────────────────────────────
const DAY_NAMES_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']

const fmtIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const todayStr = () => fmtIso(new Date())

function getMonthDays(year, month) {
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  return Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1))
}

const formatNumber = (val) => {
  if (val === undefined || val === null || isNaN(val)) return ''
  return Math.round(val).toLocaleString('ru-RU').replace(/\s/g, '\u00A0')
}

// ─── SVG Icons (matching feed) ──────────────────────────────────
const ICONS = {
  acceptance: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>,
  issued: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>,
  returns: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
  expense_var: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg>,
  custom: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2 2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  person: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  warning: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--danger)' }}><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
  skip: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-secondary)' }}><polygon points="5 4 15 12 5 20 5 4" /><line x1="19" y1="5" x2="19" y2="19" /></svg>,
  check: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>,
}

const OP_COLORS = {
  acceptance: '#cb11ab', // Фирменный малиновый WB
  issued: '#14b8a6',     // Свежий бирюзовый (не сливается с зеленой ЗП)
  returns: '#3b82f6',    // Красивый синий
  expense_var: '#ef4444',// Кораллово-красный
  custom: '#f59e0b',     // Теплый оранжевый
}

// ─── Component ─────────────────────────────────────────────────
export default function ChessGrid({
  employees,
  filterEmployee: parentFilterEmployee,
  isAdmin,
  userPermissions,
  profile,
  customRules,
  salaryConfig,
  activePvz,
  onRecordsUpdated,
  canAddForOthers,
  onAddRecord,
  onEditRecord,
  onQuickAdd,
  shiftMarkers: parentShiftMarkers,
  onDismissShift,
  onRestoreShift,
  selectedPvzIds,
  userPvzs,
  getPvzColor,
  getPvzFullName,
  getPvzShortName,
}) {
  const today = todayStr()
  const toast = useToast()
  const tour = useTour()

  // ─── State ─────────────────────────────────────────────────
  const now = new Date()
  const [viewYear, setViewYear] = useState(now.getFullYear())
  const [viewMonth, setViewMonth] = useState(now.getMonth())
  const [selectedEmployee, setSelectedEmployee] = useState(parentFilterEmployee || '')
  const [records, setRecords] = useState([])
  const [schedule, setSchedule] = useState([])
  const [loading, setLoading] = useState(true)
  const [slideDir, setSlideDir] = useState(null)

  // Quick Action Modal for single cell skips
  const [cellActionModal, setCellActionModal] = useState(null) // { date, empId, empName, opId, opTitle, isDismissed }

  // Bulk Action Modal for daily totals (multiple skips / restores)
  const [bulkActionModal, setBulkActionModal] = useState(null) // { date, empId, empName, missingTypes, dismissedTypes }
  const [selectedTypes, setSelectedTypes] = useState([])

  // Режим выбора ячеек для массового пропуска
  const [isSelectMode, setIsSelectMode] = useState(false)
  const [selectedCells, setSelectedCells] = useState([])

  const [recordsListModal, setRecordsListModal] = useState(null) // { date, empId, empName, opId, opTitle, records: [] }
  const [itemToDelete, setItemToDelete] = useState(null)

  const [salariesData, setSalariesData] = useState({})
  const [loadingSalaries, setLoadingSalaries] = useState(false)
  const [salaryDetailModal, setSalaryDetailModal] = useState(null)

  const [finesList, setFinesList] = useState([])
  const [finesModal, setFinesModal] = useState(null)

  const [localShowSalaryToAdmins, setLocalShowSalaryToAdmins] = useState(!!salaryConfig?.showSalaryToAdmins)

  const [adminViewMode, setAdminViewMode] = useState(() => {
    try {
      return localStorage.getItem('chess_admin_view_mode') || 'full'
    } catch (e) {
      return 'full'
    }
  })

  const handleSetAdminViewMode = (mode) => {
    setAdminViewMode(mode)
    if (mode === 'personal') {
      setSelectedEmployee('')
    }
    try {
      localStorage.setItem('chess_admin_view_mode', mode)
    } catch (e) {}
  }

  // Авто-открытие меню действий над ячейкой в Шахматке на шаге operations-chess-cell
  useEffect(() => {
    if (tour.isActive && tour.steps[tour.currentStep]) {
      const stepId = tour.steps[tour.currentStep].id
      if (stepId === 'operations-chess-cell') {
        if (!cellActionModal) {
          setCellActionModal({
            date: today,
            empId: 'demo-chess-emp',
            empName: 'Авакян Саркис',
            opId: 'acceptance',
            opTitle: 'Приёмка',
            isDismissed: false
          })
        }
      } else {
        if (cellActionModal && cellActionModal.empId === 'demo-chess-emp') {
          setCellActionModal(null)
        }
      }
    } else {
      if (cellActionModal && cellActionModal.empId === 'demo-chess-emp') {
        setCellActionModal(null)
      }
    }
  }, [tour.isActive, tour.currentStep, cellActionModal, today])

  useEffect(() => {
    setLocalShowSalaryToAdmins(!!salaryConfig?.showSalaryToAdmins)
  }, [salaryConfig])

  // Локальный расчет маркеров смен для выбранного месяца (чтобы они корректно подсвечивались при листании истории)
  const localOpsLogMap = useMemo(() => {
    const logMap = {}
    ;(records || []).forEach(l => {
      const key = `${l.employee_id}_${l.work_date}`
      if (!logMap[key]) logMap[key] = { total: 0, types: new Set(), dismissedTypes: [] }
      
      if (l.comment === '[DISMISSED]') {
        if (l.type) logMap[key].dismissedTypes.push(l.type)
      } else {
        logMap[key].total += (l.count || 0) + (l.count_late || 0)
        if (l.type) logMap[key].types.add(l.type)
      }
    })
    return logMap
  }, [records])

  const localShiftMarkers = useMemo(() => {
    if (!schedule.length) return null
    const empList = employees.length ? employees : (profile ? [{ id: profile.id, name: profile.full_name || profile.name || 'Я', is_legacy: false }] : [])
    if (!empList.length) return null
    const markers = {}
    const todayStrStr = fmtIso(new Date())
    const empColorMap = {}
    const EMP_COLORS = ['#14B8A6', '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899']
    empList.forEach((emp, idx) => {
      empColorMap[emp.id] = EMP_COLORS[idx % EMP_COLORS.length]
    })
    
    schedule.forEach(s => {
      if (!s.work_date) return
      const wDate = String(s.work_date).split('T')[0]
      const emp = empList.find(e => e.id === s.employee_id)
      if (!emp) return
      if (!markers[wDate]) markers[wDate] = []
      if (!markers[wDate].find(m => m.id === emp.id)) {
        const key = `${emp.id}_${wDate}`
        const logEntry = localOpsLogMap[key]
        const opsTypes = logEntry ? logEntry.types : new Set()
        const isFuture = wDate >= todayStrStr
        
        const isHelper = emp.role === 'helper' || s.shift_role === 'helper'
        const isHourlyHelper = isHelper && (salaryConfig?.helperHourlyRate > 0)
        const requiredTypes = isHourlyHelper ? [] : ['acceptance', 'issued', 'returns']
        
        const dismissedTypes = logEntry ? logEntry.dismissedTypes || [] : []
        const missingTypes = isFuture ? [] : requiredTypes.filter(t => !opsTypes.has(t) && !dismissedTypes.includes(t))
        const filled = isFuture ? null : missingTypes.length === 0
        markers[wDate] = markers[wDate] || []
        markers[wDate].push({
          id: emp.id,
          name: emp.full_name || emp.name || 'Сотрудник',
          color: empColorMap[emp.id] || '#999',
          filled,
          missingTypes,
          dismissedTypes
        })
      }
    })
    return markers
  }, [schedule, employees, localOpsLogMap, profile, salaryConfig])

  const shiftMarkers = localShiftMarkers || parentShiftMarkers

  const isCellSelected = useCallback((date, empId, opId) => {
    return selectedCells.some(c => c.date === date && c.empId === empId && c.opId === opId)
  }, [selectedCells])

  const getShiftHours = useCallback((empId, dateIso) => {
    const shift = schedule.find(s => 
      s.employee_id === empId && 
      s.work_date === dateIso &&
      (parseFloat(s.hours) > 0 || s.started_at || s.is_planned)
    )
    if (!shift) return 0
    return parseFloat(shift.hours) || 0
  }, [schedule])

  const toggleCellSelection = useCallback((date, empId, opId) => {
    setSelectedCells(prev => {
      const exists = prev.some(c => c.date === date && c.empId === empId && c.opId === opId)
      if (exists) {
        return prev.filter(c => !(c.date === date && c.empId === empId && c.opId === opId))
      } else {
        return [...prev, { date, empId, opId }]
      }
    })
  }, [])

  const toggleDayTotalSelection = useCallback((date, empId, toggleableTypes) => {
    if (!toggleableTypes || toggleableTypes.length === 0) return
    const allSelected = toggleableTypes.every(opId => isCellSelected(date, empId, opId))
    
    setSelectedCells(prev => {
      let filtered = prev.filter(c => !(c.date === date && c.empId === empId))
      if (allSelected) {
        return filtered
      } else {
        const newSelections = toggleableTypes.map(opId => ({ date, empId, opId }))
        return [...filtered, ...newSelections]
      }
    })
  }, [isCellSelected])

  const handleBulkDismissSelected = useCallback(() => {
    if (selectedCells.length === 0) return
    selectedCells.forEach(cell => {
      onDismissShift?.(cell.date, cell.empId, cell.opId)
    })
    setSelectedCells([])
    setIsSelectMode(false)
    if (onRecordsUpdated) onRecordsUpdated()
  }, [selectedCells, onDismissShift, onRecordsUpdated])

  const handleBulkRestoreSelected = useCallback(() => {
    if (selectedCells.length === 0) return
    selectedCells.forEach(cell => {
      onRestoreShift?.(cell.date, cell.empId, cell.opId)
    })
    setSelectedCells([])
    setIsSelectMode(false)
    if (onRecordsUpdated) onRecordsUpdated()
  }, [selectedCells, onRestoreShift, onRecordsUpdated])

  useEffect(() => {
    if (parentFilterEmployee !== undefined) setSelectedEmployee(parentFilterEmployee)
  }, [parentFilterEmployee])

  // Pre-check missing items when bulk modal opens
  useEffect(() => {
    if (bulkActionModal) {
      setSelectedTypes(bulkActionModal.missingTypes || [])
    } else {
      setSelectedTypes([])
    }
  }, [bulkActionModal])

  // ─── Computed ──────────────────────────────────────────────
  const monthDays = useMemo(() => getMonthDays(viewYear, viewMonth), [viewYear, viewMonth])
  const daysCount = monthDays.length
  const monthLabel = `${MONTH_NAMES[viewMonth]} ${viewYear}`
  const startDate = fmtIso(monthDays[0])
  const endDate = fmtIso(monthDays[daysCount - 1])

  const opTypes = useMemo(() => {
    const base = [
      { id: 'acceptance', title: 'Приёмка', color: OP_COLORS.acceptance, icon: ICONS.acceptance },
      { id: 'issued', title: 'Выдача', color: OP_COLORS.issued, icon: ICONS.issued },
      { id: 'returns', title: 'Возврат', color: OP_COLORS.returns, icon: ICONS.returns },
    ]
    if (isAdmin || userPermissions?.canManageOperations) {
      base.push({ id: 'expense_var', title: 'Расходник', color: OP_COLORS.expense_var, icon: ICONS.expense_var })
    }
    const custom = (customRules || []).map(r => ({
      id: `custom:${r.id}`, title: r.name, color: OP_COLORS.custom, icon: ICONS.custom, rule: r,
    }))
    return [...base, ...custom]
  }, [customRules, isAdmin, userPermissions])

  const activeEmployees = useMemo(() => (employees || []).filter(e => !e.is_legacy), [employees])

  const isOwner = profile?.role === 'owner'
  const isAdminOrAdminEmp = profile?.role === 'admin' || profile?.role === 'admin_employee'
  const canSeeSalary = isOwner || isAdminOrAdminEmp

  const showEmployeeRows = useMemo(() => {
    if (selectedEmployee) return false
    if (isOwner) return true
    if (isAdminOrAdminEmp) {
      return adminViewMode === 'full'
    }
    return canAddForOthers
  }, [selectedEmployee, isOwner, isAdminOrAdminEmp, adminViewMode, canAddForOthers])

  const loadSalaries = useCallback(async () => {
    if (!canSeeSalary || !activeEmployees.length) {
      setSalariesData({})
      return
    }
    try {
      setLoadingSalaries(true)
      const results = {}
      await Promise.all(
        activeEmployees.map(async (emp) => {
          try {
            const sal = await api.calculateAutoSalary(emp.id, startDate, endDate, { mode: 'analytics' })
            results[emp.id] = sal
          } catch (err) {
            console.error(`Error loading salary for ${emp.id}:`, err)
          }
        })
      )
      setSalariesData(results)
    } catch (err) {
      console.error('Error loading salaries:', err)
    } finally {
      setLoadingSalaries(false)
    }
  }, [canSeeSalary, activeEmployees, startDate, endDate])

  useEffect(() => {
    loadSalaries()
  }, [loadSalaries, records])

  const handleToggleShowSalaryToAdmins = async (e) => {
    const newVal = e.target.checked
    setLocalShowSalaryToAdmins(newVal)
    try {
      const updatedConfig = {
        ...salaryConfig,
        showSalaryToAdmins: newVal
      }
      await api.updateSalaryConfig(updatedConfig, profile?.id)
      toast.success(newVal ? 'Админам разрешено видеть ЗП' : 'Админам запрещено видеть ЗП')
      if (onRecordsUpdated) onRecordsUpdated()
    } catch (err) {
      console.error('Failed to update salary config', err)
      toast.error('Не удалось обновить настройки доступа к ЗП')
      setLocalShowSalaryToAdmins(!!salaryConfig?.showSalaryToAdmins)
    }
  }

  const handleShowSalaryDetail = (empId, employeeName, date, daySal) => {
    const details = []
    if (daySal.basePay > 0) details.push({ label: 'Оклад (по часам)', value: daySal.basePay, isNegative: false })
    if (daySal.acceptBonus > 0) details.push({ label: 'Приёмка товаров', value: daySal.acceptBonus, isNegative: false })
    if (daySal.issuedBonus > 0) details.push({ label: 'Выдача товаров', value: daySal.issuedBonus, isNegative: false })
    if (daySal.returnBonus > 0) details.push({ label: 'Возврат товаров', value: daySal.returnBonus, isNegative: false })
    if (daySal.customWorkBonus > 0) details.push({ label: 'Дополнительные операции', value: daySal.customWorkBonus, isNegative: false })
    if (daySal.bonuses > 0) details.push({ label: 'Премия / Бонус', value: daySal.bonuses, isNegative: false })
    if (daySal.fines > 0) details.push({ label: 'Штраф (вычет)', value: daySal.fines, isNegative: true })
    if (daySal.helperDeduction > 0) details.push({ label: 'Удержание за помощника', value: daySal.helperDeduction, isNegative: true })
    if (daySal.wbDeductions > 0) details.push({ label: 'Удержание WB (брак/подмены)', value: daySal.wbDeductions, isNegative: true })

    const dateFormatted = new Date(date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'short' })

    setSalaryDetailModal({
      employeeName,
      dateFormatted,
      details,
      total: Math.round(daySal.total)
    })
  }

  // ─── Data Loading ──────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!profile?.id || !activePvz?.id) return
    try {
      setLoading(true)
      const empId = (!isAdmin && !userPermissions?.canManageOperations)
        ? profile.id
        : (selectedEmployee || undefined)
      
      // 1. Load work logs
      const logsData = await api.getWorkLogs('all', { startDate, endDate, employeeId: empId })
      let mergedRecords = logsData || []

      // 2. Load schedule data
      let monthSchedule = []
      try {
        monthSchedule = await api.getSchedule(viewYear, viewMonth + 1)
      } catch (err) {
        console.error('ChessGrid: loadData schedule error', err)
      }
      setSchedule(monthSchedule || [])

      // 3. Load variable expenses if admin/owner
      if (isAdmin || userPermissions?.canManageOperations) {
        try {
          const expenses = await api.getExpenses(activePvz.id, startDate, endDate)
          const varExpenses = expenses.filter(e => e.type === 'variable').map(e => {
            let quantity = 1
            let cleanComment = e.comment || ''
            let authorName = 'ПВЗ (Аналитика)'

            const matchQty = cleanComment.match(/^Кол-во:\s*(\d+)\s*шт\.?\s*/)
            if (matchQty) {
              quantity = parseInt(matchQty[1], 10)
              cleanComment = cleanComment.replace(matchQty[0], '')
            }

            const matchAuthor = cleanComment.match(/\s*\(Автор:\s*(.*?)\)\s*/)
            if (matchAuthor) {
              authorName = matchAuthor[1].trim()
              cleanComment = cleanComment.replace(matchAuthor[0], '')
            }

            const emp = (employees || []).find(empObj => 
              (empObj.full_name && empObj.full_name.trim().toLowerCase() === authorName.toLowerCase()) || 
              (empObj.name && empObj.name.trim().toLowerCase() === authorName.toLowerCase())
            )

            return {
              id: 'exp_' + e.id,
              real_id: e.id,
              type: 'expense_var',
              work_date: e.date,
              created_at: e.created_at || new Date(e.date).toISOString(),
              count: parseFloat(e.amount) || 0,
              count_late: quantity,
              box_number: e.category,
              comment: cleanComment.trim(),
              employee_id: emp ? emp.id : null,
              employee_name: authorName
            }
          }).filter(e => e.employee_name !== 'ПВЗ (Аналитика)' && e.employee_id)

          if (empId) {
            mergedRecords = [...mergedRecords, ...varExpenses.filter(e => e.employee_id === empId)]
          } else {
            mergedRecords = [...mergedRecords, ...varExpenses]
          }
        } catch (err) {
          console.error('ChessGrid: loadData expenses error', err)
        }
      }

      // 4. Load fine payments & deductions for cell breakdown
      try {
        const pvzFilter = selectedPvzIds?.length > 0 ? selectedPvzIds.join(',') : activePvz?.id
        const [paymentsFines, deds] = await Promise.all([
          api.getPayments({ type: 'fine', periodStart: startDate, periodEnd: endDate, pvzId: pvzFilter }).catch(() => []),
          api.getDeductions(startDate, endDate, pvzFilter).catch(() => [])
        ])
        
        const normalizedFinePayments = (paymentsFines || []).map(p => ({
          id: p.id,
          employee_id: p.employee_id,
          date: p.period_start,
          amount: parseFloat(p.amount) || 0,
          comment: p.comment,
          created_at: p.created_at,
          created_by: p.created_by,
          type: 'fine',
          is_payment: true
        }))

        const normalizedDeductions = (deds || []).map(d => ({
          id: d.id,
          employee_id: d.employee_id,
          date: d.date,
          amount: parseFloat(d.amount) || 0,
          comment: d.comment || d.type,
          created_at: d.created_at,
          created_by: d.created_by,
          type: d.type || 'deduction',
          is_payment: false
        }))

        setFinesList([...normalizedFinePayments, ...normalizedDeductions])
      } catch (err) {
        console.error('ChessGrid: loadDeductions error', err)
      }

      setRecords(mergedRecords)
    } catch (err) {
      console.error('ChessGrid: loadData error', err)
    } finally {
      setLoading(false)
    }
  }, [profile?.id, activePvz?.id, selectedPvzIds, startDate, endDate, selectedEmployee, isAdmin, userPermissions, employees, viewYear, viewMonth])

  const handleShowFineDetail = (empId, empName, iso, totalVal) => {
    if (!totalVal || totalVal <= 0) return

    const items = finesList.filter(f => f.employee_id === empId && f.date === iso)
    const finalItems = items.length > 0 ? items : [
      {
        id: 'synthetic_fine',
        employee_id: empId,
        date: iso,
        amount: totalVal,
        comment: 'Штраф (по данным расчёта)',
        created_at: null,
        created_by: null,
        type: 'fine',
        is_payment: false
      }
    ]

    setFinesModal({
      empId,
      empName,
      date: iso,
      items: finalItems,
      totalAmount: totalVal
    })
  }

  const handleDeleteFineItem = async (item) => {
    if (!item || !item.id || item.id === 'synthetic_fine') return
    try {
      if (item.is_payment) {
        await api.deletePayment(item.id)
      } else {
        await api.deleteDeduction(item.id)
      }
      toast.success('Штраф удалён')

      setFinesList(prev => prev.filter(f => f.id !== item.id))
      
      if (finesModal) {
        const remainingItems = finesModal.items.filter(f => f.id !== item.id)
        if (remainingItems.length === 0) {
          setFinesModal(null)
        } else {
          const newTotal = remainingItems.reduce((acc, i) => acc + (parseFloat(i.amount) || 0), 0)
          setFinesModal({
            ...finesModal,
            items: remainingItems,
            totalAmount: newTotal
          })
        }
      }

      if (onRecordsUpdated) onRecordsUpdated()
      loadSalaries()
    } catch (err) {
      console.error('Error deleting fine item:', err)
      toast.error('Ошибка удаления штрафа')
    }
  }

  useEffect(() => { loadData() }, [loadData])

  // Reload data when parent signals or window event fires
  useEffect(() => {
    const handleUpdate = () => {
      loadData()
    }
    window.addEventListener('operationsUpdated', handleUpdate)
    return () => {
      window.removeEventListener('operationsUpdated', handleUpdate)
    }
  }, [loadData])

  // ─── Data Matrices ─────────────────────────────────────────
  const { empMatrix, opMatrix, empTotals, empOpTotals, opRowTotals, colTotals, grandTotal } = useMemo(() => {
    const emp = {}
    const op = {}
    const empT = {}
    const empOpT = {}
    const opRT = {}
    const colT = {}
    let grand = 0

    monthDays.forEach(d => {
      const key = fmtIso(d)
      op[key] = {}
      colT[key] = 0
    })

    records.forEach(r => {
      const date = r.work_date
      if (!op[date]) return
      const empId = r.employee_id
      const count = (r.count || 0) + (r.type === 'acceptance' ? (r.count_late || 0) : 0)

      // Op matrix
      if (!op[date][r.type]) op[date][r.type] = 0
      op[date][r.type] += count

      // Emp matrix
      if (!emp[empId]) emp[empId] = {}
      if (!emp[empId][date]) emp[empId][date] = { total: 0 }
      
      if (r.type !== 'expense_var') {
        emp[empId][date].total += count
      }
      
      if (!emp[empId][date][r.type]) emp[empId][date][r.type] = 0
      emp[empId][date][r.type] += count

      // Totals
      if (r.type !== 'expense_var') {
        if (!empT[empId]) empT[empId] = 0
        empT[empId] += count
      }

      // Emp Op Totals
      if (!empOpT[empId]) empOpT[empId] = {}
      if (!empOpT[empId][r.type]) empOpT[empId][r.type] = 0
      empOpT[empId][r.type] += count

      if (r.type !== 'expense_var') {
        colT[date] += count
        grand += count
      }
    })

    opTypes.forEach(op2 => {
      let sum = 0
      monthDays.forEach(d => { sum += (op[fmtIso(d)]?.[op2.id] || 0) })
      opRT[op2.id] = sum
    })

    return { empMatrix: emp, opMatrix: op, empTotals: empT, empOpTotals: empOpT, opRowTotals: opRT, colTotals: colT, grandTotal: grand }
  }, [records, monthDays, opTypes])

  // ─── Salary Totals Calculations ────────────────────────────────
  const { salaryDailyTotals, salaryGrandTotal } = useMemo(() => {
    const daily = {}
    let grand = 0
    if (!canSeeSalary || !salariesData) return { salaryDailyTotals: daily, salaryGrandTotal: grand }

    Object.keys(salariesData).forEach(empId => {
      const empSal = salariesData[empId]
      if (empSal && empSal.dailyDetails) {
        let empTotalUpToToday = 0;
        Object.keys(empSal.dailyDetails).forEach(iso => {
          if (iso <= today) {
            const val = Math.round(empSal.dailyDetails[iso]?.total || 0)
            if (val > 0) daily[iso] = (daily[iso] || 0) + val
            empTotalUpToToday += empSal.dailyDetails[iso].total
          }
        })
        grand += Math.max(Math.round(empTotalUpToToday), 0)
      }
    })

    return { salaryDailyTotals: daily, salaryGrandTotal: grand }
  }, [salariesData, canSeeSalary, today])

  // ─── Expansion State ─────────────────────────────────────────
  const [expandedState, setExpandedState] = useState({})
  const [hoveredDay, setHoveredDay] = useState(null)

  const isExpanded = useCallback((empId) => {
    return !!expandedState[empId]
  }, [expandedState])

  const toggleExpand = useCallback((empId) => {
    setExpandedState(prev => ({
      ...prev,
      [empId]: !isExpanded(empId)
    }))
  }, [isExpanded])

  const handleEmployeeHeaderClick = useCallback((empId, event) => {
    const wasExpanded = isExpanded(empId)
    toggleExpand(empId)

    if (!wasExpanded) {
      const nameCell = event.currentTarget
      const groupElement = nameCell.closest('.chess-employee-group')
      if (groupElement) {
        requestAnimationFrame(() => {
          const wrapper = groupElement.closest('.chess-grid-wrapper')
          if (wrapper) {
            const isWrapperScrollable = wrapper.scrollHeight > wrapper.clientHeight
            const wrapperRect = wrapper.getBoundingClientRect()
            
            if (isWrapperScrollable) {
              const groupRect = groupElement.getBoundingClientRect()
              const relativeTop = groupRect.top - wrapperRect.top + wrapper.scrollTop
              
              const gridHeader = wrapper.querySelector('.chess-grid-header')
              const headerHeight = gridHeader ? gridHeader.getBoundingClientRect().height : 48
              
              const targetScrollTop = relativeTop - headerHeight
              
              wrapper.scrollTo({
                top: targetScrollTop,
                behavior: 'smooth'
              })

              // Also make sure the wrapper itself is nicely visible in the window
              if (wrapperRect.top < 100 || wrapperRect.top > window.innerHeight - 200) {
                const absoluteWrapperTop = wrapperRect.top + window.pageYOffset
                window.scrollTo({
                  top: Math.max(0, absoluteWrapperTop - 100),
                  behavior: 'smooth'
                })
              }
            } else {
              // Scroll the window so the clicked group is positioned nicely in the viewport
              const groupRect = groupElement.getBoundingClientRect()
              const absoluteGroupTop = groupRect.top + window.pageYOffset
              
              // We target showing the name row at 120px from the top of the viewport
              window.scrollTo({
                top: Math.max(0, absoluteGroupTop - 120),
                behavior: 'smooth'
              })
            }
          }
        })
      }
    }
  }, [isExpanded, toggleExpand])

  const sortedEmployees = useMemo(() => {
    const list = (employees || []).filter(e => {
      if (!e.is_legacy) return true;
      // Show dismissed employees only if they have operations or shifts in the current view
      if (empTotals[e.id] && empTotals[e.id] > 0) return true;
      if (schedule.some(s => s.employee_id === e.id && (parseFloat(s.hours) > 0 || s.started_at || s.is_planned))) return true;
      return false;
    })
    return [...list].sort((a, b) => {
      const aTotal = empTotals[a.id] || 0
      const bTotal = empTotals[b.id] || 0

      // Put employees with operations first, empty ones at the bottom
      if (aTotal > 0 && bTotal === 0) return -1
      if (aTotal === 0 && bTotal > 0) return 1

      // Keep alphabetical order for names
      const aName = a.full_name || a.name || ''
      const bName = b.full_name || b.name || ''
      return aName.localeCompare(bName, 'ru')
    })
  }, [employees, empTotals, schedule])

  const visibleEmployees = useMemo(() => {
    if (isAdminOrAdminEmp && adminViewMode === 'personal') {
      return sortedEmployees.filter(e => e.id === profile?.id)
    }
    return sortedEmployees
  }, [sortedEmployees, isAdminOrAdminEmp, adminViewMode, profile?.id])

  const getCellPvzId = useCallback((date, empId) => {
    const cellShift = schedule.find(s => s.employee_id === empId && s.work_date === date)
    if (cellShift?.pvz_id) return cellShift.pvz_id
    const emp = employees.find(e => e.id === empId)
    if (emp?.pvz_id) return emp.pvz_id
    return (selectedPvzIds && selectedPvzIds[0]) || activePvz?.id || ''
  }, [schedule, employees, selectedPvzIds, activePvz])

  const getGroupedEmployees = useCallback(() => {
    if (!selectedPvzIds || selectedPvzIds.length <= 1) {
      return [{ pvzId: null, pvzName: null, emps: visibleEmployees }]
    }

    const groups = []
    
    selectedPvzIds.forEach(pvzId => {
      const pvzName = getPvzFullName?.(pvzId) || 'Пункт выдачи'
      const emps = visibleEmployees.filter(e => e.pvz_id === pvzId)
      if (emps.length > 0) {
        groups.push({ pvzId, pvzName, emps })
      }
    })

    const otherEmps = visibleEmployees.filter(e => !selectedPvzIds.includes(e.pvz_id))
    if (otherEmps.length > 0) {
      groups.push({ pvzId: 'other', pvzName: 'Другие сотрудники', emps: otherEmps })
    }

    if (groups.length === 0) {
      return [{ pvzId: null, pvzName: null, emps: visibleEmployees }]
    }

    return groups
  }, [selectedPvzIds, visibleEmployees, getPvzFullName])

  // ─── Navigation ────────────────────────────────────────────
  const goMonth = (dir) => {
    setSlideDir(dir > 0 ? 'right' : 'left')
    setTimeout(() => {
      setViewMonth(prev => {
        let m = prev + dir
        if (m < 0) { setViewYear(y => y - 1); return 11 }
        if (m > 11) { setViewYear(y => y + 1); return 0 }
        return m
      })
      setSlideDir(null)
    }, 150)
  }

  // ─── Cell Click Delegation ──────────────────────────────────
  const handleEmpHeaderCellClick = (date, empId) => {
    const canEdit = isAdmin || userPermissions?.canManageOperations
    if (!canEdit) return

    const marker = shiftMarkers?.[date]?.find(m => m.id === empId)
    const hasShift = !!marker
    
    const allRequired = ['acceptance', 'issued', 'returns']
    const dayRecords = records.filter(r => r.work_date === date && r.employee_id === empId)
    const missingRequired = allRequired.filter(t => !dayRecords.some(r => r.type === t))
    const dismissed = marker?.dismissedTypes || []
    const allToggleable = [...new Set([...missingRequired, ...dismissed])]

    if (isSelectMode) {
      if (hasShift && allToggleable.length > 0) {
        toggleDayTotalSelection(date, empId, allToggleable)
      }
      return
    }

    const isFuture = date > today

    if (hasShift && !isFuture && missingRequired.length > 0) {
      const emp = employees.find(e => e.id === empId)
      setBulkActionModal({
        date,
        empId,
        empName: emp ? (emp.full_name || emp.name) : 'Сотрудник',
        missingTypes: marker ? (marker.missingTypes || []) : missingRequired,
        dismissedTypes: marker ? (marker.dismissedTypes || []) : []
      })
    } else {
      onQuickAdd?.(date, empId, getCellPvzId(date, empId))
    }
  }

  const handleCellClick = (date, empId, opId) => {
    const canEdit = isAdmin || userPermissions?.canManageOperations
    if (!canEdit) return

    const marker = shiftMarkers?.[date]?.find(m => m.id === empId)
    const hasShift = !!marker
    const isRequired = ['acceptance', 'issued', 'returns'].includes(opId)

    // Check if we have multiple records
    const dayRecords = records.filter(r => r.work_date === date && r.employee_id === empId && r.type === opId)
    if (dayRecords.length >= 1) {
      const emp = employees.find(e => e.id === empId)
      const op = opTypes.find(o => o.id === opId)
      setRecordsListModal({
        date,
        empId,
        empName: emp ? (emp.full_name || emp.name) : 'Сотрудник',
        opId,
        opTitle: op ? op.title : 'Операция',
        records: dayRecords
      })
      return
    }

    const existing = null; // single existing is handled above

    if (isSelectMode) {
      if (hasShift && isRequired && !existing && date <= today) {
        toggleCellSelection(date, empId, opId)
      }
      return
    }

    if (hasShift && isRequired && !existing && date <= today) {
      // Past/present shift, missing required op -> open skip/fill actions modal
      const isDismissed = marker.dismissedTypes?.includes(opId)
      const emp = employees.find(e => e.id === empId)
      const op = opTypes.find(o => o.id === opId)
      
      setCellActionModal({
        date,
        empId,
        empName: emp ? (emp.full_name || emp.name) : 'Сотрудник',
        opId,
        opTitle: op ? op.title : 'Операция',
        isDismissed
      })
    } else {
      if (existing) {
        onEditRecord?.(existing)
      } else {
        onAddRecord?.(date, empId, opId, getCellPvzId(date, empId))
      }
    }
  }

  const handleOpCellClick = (date, op) => {
    const canEdit = isAdmin || userPermissions?.canManageOperations || date === today
    if (!canEdit && !isAdmin) return
    const empId = selectedEmployee || profile?.id

    const marker = shiftMarkers?.[date]?.find(m => m.id === empId)
    const hasShift = !!marker
    const isRequired = ['acceptance', 'issued', 'returns'].includes(op.id)

    // Check if we have multiple records
    const dayRecords = records.filter(r => r.work_date === date && r.employee_id === empId && r.type === op.id)
    if (dayRecords.length >= 1) {
      const emp = employees.find(e => e.id === empId)
      setRecordsListModal({
        date,
        empId,
        empName: emp ? (emp.full_name || emp.name) : 'Сотрудник',
        opId: op.id,
        opTitle: op.title,
        records: dayRecords
      })
      return
    }

    const existing = null; // single existing is handled above

    if (isSelectMode) {
      if (hasShift && isRequired && !existing && date <= today) {
        toggleCellSelection(date, empId, op.id)
      }
      return
    }

    if (hasShift && isRequired && !existing && date <= today) {
      const isDismissed = marker.dismissedTypes?.includes(op.id)
      const emp = employees.find(e => e.id === empId)
      
      setCellActionModal({
        date,
        empId,
        empName: emp ? (emp.full_name || emp.name) : 'Сотрудник',
        opId: op.id,
        opTitle: op.title,
        isDismissed
      })
    } else {
      if (existing) {
        onEditRecord?.(existing)
      } else {
        onAddRecord?.(date, empId, op.id, getCellPvzId(date, empId))
      }
    }
  }

  // ─── Single Cell Actions ────────────────────────────────────
  const triggerAddFromModal = () => {
    if (!cellActionModal) return
    onAddRecord?.(cellActionModal.date, cellActionModal.empId, cellActionModal.opId, getCellPvzId(cellActionModal.date, cellActionModal.empId))
    setCellActionModal(null)
  }

  const triggerDismissFromModal = () => {
    if (!cellActionModal) return
    onDismissShift?.(cellActionModal.date, cellActionModal.empId, cellActionModal.opId)
    setCellActionModal(null)
  }

  const triggerRestoreFromModal = () => {
    if (!cellActionModal) return
    onRestoreShift?.(cellActionModal.date, cellActionModal.empId, cellActionModal.opId)
    setCellActionModal(null)
  }

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return
    try {
      if (itemToDelete.type === 'expense_var') {
        await api.deleteExpense(itemToDelete.real_id || itemToDelete.id.replace('exp_', ''))
      } else {
        await api.deleteWorkLog(itemToDelete.id)
      }
      toast.success('Запись удалена')
      
      setRecords(prev => prev.filter(r => r.id !== itemToDelete.id))
      
      if (recordsListModal) {
        const updatedRecords = recordsListModal.records.filter(r => r.id !== itemToDelete.id)
        if (updatedRecords.length === 0) {
          setRecordsListModal(null)
        } else {
          setRecordsListModal({
            ...recordsListModal,
            records: updatedRecords
          })
        }
      }
      
      if (onRecordsUpdated) onRecordsUpdated()
    } catch (err) {
      console.error(err)
      toast.error('Ошибка удаления')
    } finally {
      setItemToDelete(null)
    }
  }

  // ─── Bulk Modal Actions ─────────────────────────────────────
  const handleBulkDismiss = () => {
    if (!bulkActionModal) return
    const { date, empId } = bulkActionModal
    selectedTypes.forEach(type => {
      onDismissShift?.(date, empId, type)
    })
    setBulkActionModal(null)
  }

  const handleBulkRestore = () => {
    if (!bulkActionModal) return
    const { date, empId } = bulkActionModal
    selectedTypes.forEach(type => {
      onRestoreShift?.(date, empId, type)
    })
    setBulkActionModal(null)
  }

  // Helper to check if employee has shift on date
  const checkHasShift = useCallback((empId, dateIso) => {
    return schedule.some(s => 
      s.employee_id === empId && 
      s.work_date === dateIso && 
      (parseFloat(s.hours) > 0 || s.started_at || s.is_planned)
    )
  }, [schedule])

  // ─── Grid style ────────────────────────────────────────────
  const gridCols = `var(--chess-name-width, 180px) repeat(${daysCount}, var(--chess-day-width, minmax(24px, 1fr))) var(--chess-total-width, 60px)`

  // ─── Render ────────────────────────────────────────────────
  return (
    <div className="chess-grid-container animate-fade-in">

      {/* ── Navigation ── */}
      <div className="schedule-month-nav">
        <button className="btn btn-ghost btn-sm" onClick={() => goMonth(-1)}>← Пред.</button>
        <span className="schedule-month-title">{monthLabel}</span>
        <button className="btn btn-ghost btn-sm" onClick={() => goMonth(1)}>След. →</button>
      </div>

      {/* ── Employee selector & Skip Mode button ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        {canAddForOthers && activeEmployees.length > 0 && (!isAdminOrAdminEmp || adminViewMode === 'full') ? (
          <div className="chess-employee-selector" style={{ flex: 1, minWidth: '280px', display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            <CustomSelect
              value={selectedEmployee}
              onChange={setSelectedEmployee}
              placeholder="Все сотрудники"
              options={[
                { value: '', label: 'Все сотрудники' },
                ...activeEmployees.map(emp => ({
                  value: emp.id,
                  label: emp.full_name || emp.name || 'Сотрудник',
                })),
              ]}
            />
          </div>
        ) : <div />}

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {isAdminOrAdminEmp && (
            <div className="chess-view-switcher" style={{ display: 'inline-flex', gap: '4px', background: 'var(--surface-color)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '3px', height: '36px', alignItems: 'center', boxSizing: 'border-box' }}>
              <button
                type="button"
                className={`chess-view-btn ${adminViewMode === 'personal' ? 'active' : ''}`}
                onClick={() => handleSetAdminViewMode('personal')}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  borderRadius: '9px',
                  border: 'none',
                  background: adminViewMode === 'personal' ? 'var(--wb-primary)' : 'transparent',
                  color: adminViewMode === 'personal' ? '#fff' : 'var(--text-secondary)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap'
                }}
              >
                Личный вид
              </button>
              <button
                type="button"
                className={`chess-view-btn ${adminViewMode === 'full' ? 'active' : ''}`}
                onClick={() => handleSetAdminViewMode('full')}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  borderRadius: '9px',
                  border: 'none',
                  background: adminViewMode === 'full' ? 'var(--wb-primary)' : 'transparent',
                  color: adminViewMode === 'full' ? '#fff' : 'var(--text-secondary)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap'
                }}
              >
                Все сотрудники
              </button>
            </div>
          )}

          {isOwner && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer', userSelect: 'none', background: 'var(--surface-color)', padding: '8px 16px', borderRadius: '12px', border: '1px solid var(--border-color)', height: '36px', margin: 0 }}>
              <input 
                type="checkbox"
                checked={localShowSalaryToAdmins}
                onChange={handleToggleShowSalaryToAdmins}
                style={{ accentColor: 'var(--wb-primary)', width: '15px', height: '15px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 500 }}>Показывать ЗП админам</span>
            </label>
          )}

          {(isAdmin || userPermissions?.canManageOperations) && (
            <button 
              className={`btn ${isSelectMode ? 'btn-primary' : 'btn-secondary'} btn-sm`}
              onClick={() => {
                setIsSelectMode(!isSelectMode)
                setSelectedCells([])
              }}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '8px 16px', borderRadius: '12px', whiteSpace: 'nowrap', height: '36px' }}
            >
              {isSelectMode ? (
                <>
                  {ICONS.check}
                  <span>Выбор активен</span>
                </>
              ) : (
                <>
                  {ICONS.skip}
                  <span>Режим пропуска</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* ── Grid ── */}
      <div className={`chess-table-wrapper ${slideDir ? `chess-slide-${slideDir}` : ''}`}>
        {loading ? (
          <div className="chess-skeleton">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="chess-skeleton-row">
                <div className="skeleton chess-skeleton-label" />
                {[...Array(15)].map((_, j) => <div key={j} className="skeleton chess-skeleton-cell" />)}
              </div>
            ))}
          </div>
        ) : (
          <div className="chess-grid-wrapper">
            <div className="chess-grid" style={{ '--chess-days': daysCount }}>

              {/* ── Header Row ── */}
              <div className="chess-grid-header" style={{ gridTemplateColumns: gridCols }}>
                <div className="chess-grid-cell chess-name-cell">
                  {showEmployeeRows ? 'Сотрудник' : 'Операция'}
                </div>
                {monthDays.map(d => {
                  const iso = fmtIso(d)
                  const isToday = iso === today
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6
                  
                  // Сбор статистики для тултипа
                  const dayTotalOps = colTotals[iso] || 0
                  const empsOnDay = visibleEmployees.filter(e => (empMatrix[e.id] && empMatrix[e.id][iso]) || (shiftMarkers?.[iso]?.find(m => m.id === e.id)))
                  const statsParts = empsOnDay.map(e => {
                    const ops = empMatrix[e.id]?.[iso]
                    if (!ops) return null
                    const parts = []
                    opTypes.forEach(op => {
                      if (ops[op.id]) parts.push(`${op.title}: ${ops[op.id]}`)
                    })
                    if (parts.length === 0) return null
                    return { name: e.name || e.full_name, parts }
                  }).filter(Boolean)

                  return (
                    <div 
                      key={iso} 
                      className={`chess-grid-cell chess-day-header ${isToday ? 'chess-day-today' : ''} ${isWeekend ? 'chess-day-weekend' : ''}`}
                      style={{ position: 'relative', overflow: 'visible' }}
                      onMouseEnter={() => setHoveredDay(iso)}
                      onMouseLeave={() => setHoveredDay(null)}
                    >
                      <span className={`chess-day-num ${isToday ? 'chess-day-num-today' : ''}`}>{d.getDate()}</span>
                      <span className="chess-day-name">{DAY_NAMES_SHORT[d.getDay()]}</span>
                      
                      {hoveredDay === iso && (
                        <div style={{
                          position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                          marginTop: '8px', padding: '12px', background: 'var(--surface)', border: '1px solid var(--surface-border)',
                          borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', zIndex: 100,
                          minWidth: '220px', fontSize: '13px', textAlign: 'left', pointerEvents: 'none', color: 'var(--text-primary)'
                        }}>
                          <div style={{ fontWeight: 700, marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid var(--surface-border)' }}>
                            {d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 400, marginTop: '2px' }}>Всего операций: {dayTotalOps}</div>
                          </div>
                          {statsParts.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {statsParts.map((stat, i) => (
                                <div key={i}>
                                  <div style={{ fontWeight: 600, color: 'var(--wb-primary)', fontSize: '12px' }}>{stat.name}</div>
                                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{stat.parts.join(', ')}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ color: 'var(--text-secondary)', fontSize: '12px', fontStyle: 'italic' }}>Нет операций</div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
                <div className="chess-grid-cell chess-total-header">Итого</div>
              </div>

              {/* ── Data Rows ── */}
              {showEmployeeRows ? (
                /* ─── Employee rows with nested operations ─── */
                (() => {
                  const grouped = getGroupedEmployees()
                  return grouped.flatMap(group => {
                    const groupRows = []
                    if (group.pvzName) {
                      const pvzColor = group.pvzId && group.pvzId !== 'other' ? getPvzColor(group.pvzId) : 'var(--text-tertiary)';
                      groupRows.push(
                        <div 
                          key={`pvz-group-${group.pvzId}`}
                          className="schedule-pvz-group-row"
                          style={{ borderLeft: `3px solid ${pvzColor}` }}
                        >
                          <span className="schedule-pvz-group-icon" style={{ color: pvzColor }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                              <polyline points="9 22 9 12 15 12 15 22"/>
                            </svg>
                          </span>
                          <span className="schedule-pvz-group-title">{group.pvzName}</span>
                          <span className="schedule-pvz-group-count">({group.emps.length})</span>
                        </div>
                      )
                    }
                    group.emps.forEach((emp, empIdx) => {
                      const empData = empMatrix[emp.id] || {}
                      const total = empTotals[emp.id] || 0
                      const isExpandedRow = isExpanded(emp.id)
                      groupRows.push(
                        <div key={emp.id} className="chess-employee-group">
                      {/* Employee header row showing total operations count */}
                      <div className={`chess-grid-row chess-employee-row-header ${total === 0 ? 'chess-employee-row-header-empty' : ''}`} style={{ gridTemplateColumns: gridCols, animationDelay: `${empIdx * 80}ms` }}>
                        <div className="chess-grid-cell chess-name-cell chess-employee-name-header" onClick={(e) => handleEmployeeHeaderClick(emp.id, e)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          <span className={`chess-chevron ${isExpandedRow ? 'expanded' : ''}`}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                          </span>
                          <span className="chess-emp-avatar">{ICONS.person}</span>
                          <div className="chess-emp-name-wrap">
                            <span className="chess-emp-name" title={emp.full_name || emp.name}>{emp.full_name || emp.name}</span>
                            {total === 0 && <span className="chess-empty-badge">Нет операций</span>}
                          </div>
                        </div>
                        {monthDays.map(d => {
                          const iso = fmtIso(d)
                          const val = empData[iso]?.total || 0
                          const isToday = iso === today
                          const hasData = val > 0
                          const isFuture = iso > today

                          // Get shift status from parent-computed shiftMarkers
                          const marker = shiftMarkers?.[iso]?.find(m => m.id === emp.id)
                          const hasShift = !!marker
                          
                          let cellClass = ''
                          let cellContent = null
                          let cellTitle = 'Нажмите для быстрого добавления'

                          const allRequired = ['acceptance', 'issued', 'returns']
                          const dayRecords = records.filter(r => r.work_date === iso && r.employee_id === emp.id)
                          const missingRequired = allRequired.filter(t => !dayRecords.some(r => r.type === t))
                          const dismissed = marker?.dismissedTypes || []
                          const allToggleable = [...new Set([...missingRequired, ...dismissed])]

                          if (isSelectMode && hasShift && val === 0 && allToggleable.length > 0 && iso <= today) {
                            const isChecked = allToggleable.every(t => isCellSelected(iso, emp.id, t))
                            const allDismissed = allToggleable.every(t => dismissed.includes(t))
                            cellClass = 'chess-cell-selection-mode'
                            cellContent = (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', width: '100%' }}>
                                <input 
                                  type="checkbox"
                                  checked={isChecked}
                                  readOnly
                                  style={{ width: '14px', height: '14px', accentColor: 'var(--wb-primary)', cursor: 'pointer', pointerEvents: 'none' }}
                                />
                                {allDismissed ? ICONS.skip : ICONS.warning}
                              </div>
                            )
                            cellTitle = `Выбрать все операции за этот день (${allToggleable.length})`
                          } else {
                            if (hasShift) {
                              const hasMissing = marker.missingTypes && marker.missingTypes.length > 0
                              
                              if (isFuture) {
                                cellClass = 'chess-cell-shift-planned'
                                cellContent = <span className="chess-empty">—</span>
                                cellTitle = `${emp.full_name}: запланирована смена`
                              } else if (hasMissing) {
                                cellClass = 'chess-cell-shift-warning animate-pulse'
                                cellContent = (
                                  <span className="chess-val-warning" style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                                    {val} {ICONS.warning}
                                  </span>
                                )
                                const typeLabels = { acceptance: 'Приёмка', issued: 'Выдача', returns: 'Возврат' }
                                const missingLabels = marker.missingTypes.map(t => typeLabels[t] || t).join(', ')
                                cellTitle = `${emp.full_name}: смена отработана частично (всего ${val} операций), но не внесены: ${missingLabels}!`
                              } else {
                                cellClass = 'chess-cell-shift-ok'
                                cellContent = val > 0 ? <span className="chess-val-total">{val}</span> : <span className="chess-empty">—</span>
                                cellTitle = val > 0 
                                  ? `${emp.full_name}: смена отработана (всего ${val} операций)`
                                  : `${emp.full_name}: смена (все требуемые операции пропущены)`
                              }
                            } else {
                              if (hasData) {
                                cellClass = 'chess-cell-filled-total'
                                cellContent = <span className="chess-val-total">{val}</span>
                                cellTitle = `${emp.emp_name || emp.full_name}: внесено ${val} операций вне смены`
                              } else {
                                cellContent = <span className="chess-empty">—</span>
                              }
                            }
                          }

                          return (
                            <div
                              key={iso}
                              className={`chess-grid-cell chess-data-cell chess-cell-total-day ${isToday ? 'chess-cell-today' : ''} ${cellClass}`}
                              onClick={() => handleEmpHeaderCellClick(iso, emp.id)}
                              title={cellTitle}
                            >
                              {cellContent}
                            </div>
                          )
                        })}
                        <div className="chess-grid-cell chess-total-cell chess-employee-total-header">
                          <span className="chess-total-val">{total}</span>
                        </div>
                      </div>

                      {/* Operation rows for this employee wrapped in accordion container for smooth transitions */}
                      <div className={`chess-employee-details-container ${isExpandedRow ? 'expanded' : ''}`}>
                        <div className="chess-employee-details-content">
                          {opTypes.map((op, opIdx) => {
                            const valOpTotal = empOpTotals[emp.id]?.[op.id] || 0
                            return (
                              <div key={op.id} className="chess-grid-row chess-employee-row-op" style={{ gridTemplateColumns: gridCols, animationDelay: `${empIdx * 80 + (opIdx + 1) * 20}ms` }}>
                                <div className="chess-grid-cell chess-name-cell chess-employee-op-name" style={{ borderLeft: `4px solid ${op.color}`, paddingLeft: '24px' }}>
                                  <span className="chess-op-icon-wrap" style={{ color: op.color }}>{op.icon}</span>
                                  <span className="chess-op-name" style={{ color: `color-mix(in srgb, ${op.color} 85%, var(--text-primary))`, fontWeight: 600 }}>{op.title}</span>
                                </div>
                                {monthDays.map(d => {
                                  const iso = fmtIso(d)
                                  const val = empData[iso]?.[op.id] || 0
                                  const isToday = iso === today
                                  const hasData = val > 0
                                  const isFuture = iso > today

                                  const marker = shiftMarkers?.[iso]?.find(m => m.id === emp.id)
                                  const hasShift = !!marker
                                  const isHelper = emp.role === 'helper'
                                  const isHourlyHelper = isHelper && (salaryConfig?.helperHourlyRate > 0)
                                  const isRequired = isHourlyHelper ? false : ['acceptance', 'issued', 'returns'].includes(op.id)
                                  
                                  let cellClass = ''
                                  let cellContent = null
                                  let cellTitle = 'Нажмите для ввода'

                                  if (isSelectMode && hasShift && isRequired && !hasData && !isFuture) {
                                    const isChecked = isCellSelected(iso, emp.id, op.id)
                                    const isDismissed = marker.dismissedTypes?.includes(op.id)
                                    cellClass = 'chess-cell-selection-mode'
                                    cellContent = (
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', width: '100%' }}>
                                        <input 
                                          type="checkbox"
                                          checked={isChecked}
                                          readOnly
                                          style={{ width: '14px', height: '14px', accentColor: 'var(--wb-primary)', cursor: 'pointer', pointerEvents: 'none' }}
                                        />
                                        {isDismissed ? ICONS.skip : ICONS.warning}
                                      </div>
                                    )
                                    cellTitle = `Выбрать ${op.title}`
                                  } else {
                                    if (hasData) {
                                      cellClass = 'chess-cell-filled-op'
                                      cellContent = <span className="chess-val-op" style={{ color: op.color, fontWeight: 700 }}>{formatNumber(val)}</span>
                                      cellTitle = `${emp.full_name} (${op.title}): ${formatNumber(val)}`
                                    } else if (hasShift && isRequired && !isFuture) {
                                      const isDismissed = marker.dismissedTypes?.includes(op.id)
                                      if (isDismissed) {
                                        cellClass = 'chess-cell-op-dismissed'
                                        cellContent = <span className="chess-empty-dismissed" style={{ display: 'inline-flex', alignItems: 'center' }}>{ICONS.skip}</span>
                                        cellTitle = `${emp.full_name} (${op.title}): пропущено`
                                      } else {
                                        cellClass = 'chess-cell-op-warning animate-pulse'
                                        cellContent = <span className="chess-val-op-warning" style={{ display: 'inline-flex', alignItems: 'center' }}>{ICONS.warning}</span>
                                        cellTitle = `${emp.full_name} (${op.title}): требуется заполнить!`
                                      }
                                    } else {
                                      cellContent = <span className="chess-empty">—</span>
                                    }
                                  }

                                  return (
                                    <div
                                      key={iso}
                                      className={`chess-grid-cell chess-data-cell chess-cell-op ${isToday ? 'chess-cell-today' : ''} ${cellClass}`}
                                      style={{
                                        '--op-color': op.color,
                                        ...(hasData ? { background: `color-mix(in srgb, ${op.color} 8%, transparent)` } : {})
                                      }}
                                      onClick={() => handleCellClick(iso, emp.id, op.id)}
                                      title={cellTitle}
                                    >
                                      {cellContent}
                                    </div>
                                  )
                                })}
                                <div className="chess-grid-cell chess-total-cell chess-employee-op-total" style={valOpTotal > 0 ? { background: `color-mix(in srgb, ${op.color} 8%, transparent)` } : {}}>
                                  <span className="chess-total-val-op" style={{ color: op.color, fontWeight: 800 }}>{formatNumber(valOpTotal)}</span>
                                </div>
                              </div>
                            )
                          })}

                          <div className="chess-grid-row chess-employee-row-hours" style={{ gridTemplateColumns: gridCols, background: 'rgba(59, 130, 246, 0.02)', borderBottom: '1px solid var(--surface-border)' }}>
                            <div className="chess-grid-cell chess-name-cell chess-employee-op-name" style={{ color: 'var(--text-secondary)' }}>
                              <span className="chess-op-icon-wrap" style={{ color: 'var(--text-secondary)' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                              </span>
                              <span className="chess-op-name">Часы</span>
                            </div>
                            {monthDays.map(d => {
                              const iso = fmtIso(d)
                              const isToday = iso === today
                              const hours = getShiftHours(emp.id, iso)
                              return (
                                <div
                                  key={iso}
                                  className={`chess-grid-cell chess-data-cell ${isToday ? 'chess-cell-today' : ''}`}
                                  style={{ fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default' }}
                                  title={`${emp.full_name || emp.name} за ${new Date(iso).toLocaleDateString('ru')}: отработано часов: ${hours}`}
                                >
                                  {hours > 0 ? (
                                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{hours}</span>
                                  ) : (
                                    <span className="chess-empty">—</span>
                                  )}
                                </div>
                              )
                            })}
                            <div className="chess-grid-cell chess-total-cell chess-employee-op-total" style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
                              <span className="chess-total-val-op">
                                {monthDays.reduce((acc, d) => acc + getShiftHours(emp.id, fmtIso(d)), 0)}&nbsp;ч
                              </span>
                            </div>
                          </div>

                          {canSeeSalary && (
                            <div className="chess-grid-row chess-employee-row-salary" style={{ gridTemplateColumns: gridCols, background: 'rgba(16, 185, 129, 0.03)' }}>
                              <div className="chess-grid-cell chess-name-cell chess-employee-op-name" style={{ color: 'var(--success)', fontWeight: 600 }}>
                                <span className="chess-op-icon-wrap" style={{ color: 'var(--success)' }}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                                </span>
                                <span className="chess-op-name">Зарплата, ₽</span>
                              </div>
                              {monthDays.map(d => {
                                const iso = fmtIso(d)
                                const isToday = iso === today
                                const daySal = salariesData[emp.id]?.dailyDetails?.[iso]
                                const val = daySal ? Math.round(daySal.total) : 0
                                
                                let cellTitle = 'Нет начислений за этот день'
                                if (daySal && val > 0) {
                                  const parts = []
                                  if (daySal.basePay > 0) parts.push(`Оклад: ${formatNumber(daySal.basePay)} ₽`)
                                  if (daySal.acceptBonus > 0) parts.push(`Приёмка: +${formatNumber(daySal.acceptBonus)} ₽`)
                                  if (daySal.issuedBonus > 0) parts.push(`Выдача: +${formatNumber(daySal.issuedBonus)} ₽`)
                                  if (daySal.returnBonus > 0) parts.push(`Возвраты: +${formatNumber(daySal.returnBonus)} ₽`)
                                  if (daySal.customWorkBonus > 0) parts.push(`Доп. правила: +${formatNumber(daySal.customWorkBonus)} ₽`)
                                  if (daySal.bonuses > 0) parts.push(`Премия: +${formatNumber(daySal.bonuses)} ₽`)
                                  if (daySal.fines > 0) parts.push(`Штраф: -${formatNumber(daySal.fines)} ₽`)
                                  if (daySal.helperDeduction > 0) parts.push(`За помощника: -${formatNumber(daySal.helperDeduction)} ₽`)
                                  if (daySal.wbDeductions > 0) parts.push(`Удержание WB: -${formatNumber(daySal.wbDeductions)} ₽`)
                                  cellTitle = `${emp.full_name || emp.name} за ${new Date(iso).toLocaleDateString('ru')}:\n${parts.join('\n')}\nИтого: ${formatNumber(val)} ₽`
                                }

                                return (
                                  <div
                                    key={iso}
                                    className={`chess-grid-cell chess-data-cell chess-cell-salary ${isToday ? 'chess-cell-today' : ''}`}
                                    style={{ fontSize: '11px', cursor: (daySal && val > 0) ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    title={cellTitle}
                                    onClick={() => {
                                      if (daySal && val > 0) {
                                        handleShowSalaryDetail(emp.id, emp.full_name || emp.name, iso, daySal)
                                      }
                                    }}
                                  >
                                    {val > 0 ? (
                                      <span style={{ color: 'var(--success)', fontWeight: 600 }}>{formatNumber(val)}</span>
                                    ) : (
                                      <span className="chess-empty">—</span>
                                    )}
                                  </div>
                                )
                              })}
                              <div className="chess-grid-cell chess-total-cell chess-employee-op-total" style={{ color: 'var(--success)', fontWeight: 700 }}>
                                <span className="chess-total-val-op">
                                  {salariesData[emp.id] ? formatNumber(Math.max(salariesData[emp.id].total, 0)) : 0}&nbsp;₽
                                </span>
                              </div>
                            </div>
                          )}

                          {canSeeSalary && (() => {
                            const hasBonuses = monthDays.some(d => (salariesData[emp.id]?.dailyDetails?.[fmtIso(d)]?.bonuses || 0) > 0)
                            const hasFines = monthDays.some(d => (salariesData[emp.id]?.dailyDetails?.[fmtIso(d)]?.fines || 0) > 0)
                            
                            return (
                              <>
                                {hasBonuses && (
                                  <div className="chess-grid-row chess-employee-row-bonus" style={{ gridTemplateColumns: gridCols, animationDelay: '100ms' }}>
                                    <div className="chess-grid-cell chess-name-cell chess-employee-op-name" style={{ borderLeft: '4px solid var(--success)', paddingLeft: '24px' }}>
                                      <span className="chess-op-icon-wrap" style={{ color: 'var(--success)' }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                                      </span>
                                      <span className="chess-op-name" style={{ color: 'var(--text-primary)', fontWeight: 500 }}>Премия</span>
                                    </div>
                                    {monthDays.map(d => {
                                      const iso = fmtIso(d)
                                      const val = salariesData[emp.id]?.dailyDetails?.[iso]?.bonuses || 0
                                      const isToday = iso === today
                                      return (
                                        <div key={iso} className={`chess-grid-cell chess-data-cell ${isToday ? 'chess-cell-today' : ''}`} style={{ fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default' }}>
                                          {val > 0 ? <span style={{ color: 'var(--success)', fontWeight: 600 }}>+{formatNumber(val)}</span> : <span className="chess-empty">—</span>}
                                        </div>
                                      )
                                    })}
                                    <div className="chess-grid-cell chess-total-cell chess-employee-op-total" style={{ fontWeight: 600 }}>
                                      <span className="chess-total-val-op" style={{ color: 'var(--success)' }}>
                                        {formatNumber(monthDays.reduce((acc, d) => acc + (salariesData[emp.id]?.dailyDetails?.[fmtIso(d)]?.bonuses || 0), 0))}
                                      </span>
                                    </div>
                                  </div>
                                )}
                                {hasFines && (
                                  <div className="chess-grid-row chess-employee-row-fine" style={{ gridTemplateColumns: gridCols, animationDelay: '120ms' }}>
                                    <div className="chess-grid-cell chess-name-cell chess-employee-op-name" style={{ borderLeft: '4px solid var(--danger)', paddingLeft: '24px' }}>
                                      <span className="chess-op-icon-wrap" style={{ color: 'var(--danger)' }}>{ICONS.warning}</span>
                                      <span className="chess-op-name" style={{ color: 'var(--text-primary)', fontWeight: 500 }}>Штраф</span>
                                    </div>
                                    {monthDays.map(d => {
                                      const iso = fmtIso(d)
                                      const val = salariesData[emp.id]?.dailyDetails?.[iso]?.fines || 0
                                      const isToday = iso === today
                                      const hasFine = val > 0
                                      const empName = emp.full_name || emp.name || 'Сотрудник'
                                      return (
                                        <div
                                          key={iso}
                                          className={`chess-grid-cell chess-data-cell ${isToday ? 'chess-cell-today' : ''}`}
                                          style={{
                                            fontSize: '11px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: hasFine ? 'pointer' : 'default'
                                          }}
                                          title={hasFine ? `${empName}: Штрафы за ${new Date(iso).toLocaleDateString('ru-RU')}: -${formatNumber(val)} ₽ (нажмите для деталей)` : 'Штрафов нет'}
                                          onClick={() => {
                                            if (hasFine) {
                                              handleShowFineDetail(emp.id, empName, iso, val)
                                            }
                                          }}
                                        >
                                          {hasFine ? <span style={{ color: 'var(--danger)', fontWeight: 600 }}>-{formatNumber(val)}</span> : <span className="chess-empty">—</span>}
                                        </div>
                                      )
                                    })}
                                    <div className="chess-grid-cell chess-total-cell chess-employee-op-total" style={{ fontWeight: 600 }}>
                                      <span className="chess-total-val-op" style={{ color: 'var(--danger)' }}>
                                        {formatNumber(monthDays.reduce((acc, d) => acc + (salariesData[emp.id]?.dailyDetails?.[fmtIso(d)]?.fines || 0), 0))}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </>
                            )
                          })()}
                        </div>
                      </div>
                    </div>
                  )
                })
                return groupRows
              })
            })()
          ) : (
                /* ─── Operation type rows (single employee selected) ─── */
                <>
                  {opTypes.map((op, idx) => {
                    const empId = selectedEmployee || profile?.id
                    const valOpTotal = empOpTotals[empId]?.[op.id] || 0
                    return (
                      <div key={op.id} className="chess-grid-row" style={{ gridTemplateColumns: gridCols, animationDelay: `${idx * 30}ms` }}>
                        <div className="chess-grid-cell chess-name-cell" style={{ borderLeft: `4px solid ${op.color}`, paddingLeft: '8px' }}>
                          <span className="chess-op-icon-wrap" style={{ color: op.color }}>{op.icon}</span>
                          <span className="chess-op-name" style={{ color: `color-mix(in srgb, ${op.color} 85%, var(--text-primary))`, fontWeight: 600, marginLeft: '6px' }}>{op.title}</span>
                        </div>
                        {monthDays.map(d => {
                          const iso = fmtIso(d)
                          const val = empMatrix[empId]?.[iso]?.[op.id] || 0
                          const isToday = iso === today
                          const hasData = val > 0
                          const isFuture = iso > today

                          const marker = shiftMarkers?.[iso]?.find(m => m.id === empId)
                          const hasShift = !!marker
                          const isRequired = ['acceptance', 'issued', 'returns'].includes(op.id)
                          
                          let cellClass = ''
                          let cellContent = null
                          let cellTitle = 'Нажмите для ввода'

                          if (isSelectMode && hasShift && isRequired && !hasData && !isFuture) {
                            const isChecked = isCellSelected(iso, empId, op.id)
                            const isDismissed = marker.dismissedTypes?.includes(op.id)
                            cellClass = 'chess-cell-selection-mode'
                            cellContent = (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', width: '100%' }}>
                                <input 
                                  type="checkbox"
                                  checked={isChecked}
                                  readOnly
                                  style={{ width: '14px', height: '14px', accentColor: 'var(--wb-primary)', cursor: 'pointer', pointerEvents: 'none' }}
                                />
                                {isDismissed ? ICONS.skip : ICONS.warning}
                              </div>
                            )
                            cellTitle = `Выбрать ${op.title}`
                          } else {
                            if (hasData) {
                              cellClass = 'chess-cell-filled'
                              cellContent = <span className="chess-val" style={{ color: op.color, fontWeight: 700 }}>{formatNumber(val)}</span>
                              cellTitle = `${op.title}: ${formatNumber(val)}`
                            } else if (hasShift && isRequired && !isFuture) {
                              const isDismissed = marker.dismissedTypes?.includes(op.id)
                              if (isDismissed) {
                                cellClass = 'chess-cell-op-dismissed'
                                cellContent = <span className="chess-empty-dismissed" style={{ display: 'inline-flex', alignItems: 'center' }}>{ICONS.skip}</span>
                                cellTitle = `${op.title}: пропущено`
                              } else {
                                cellClass = 'chess-cell-op-warning animate-pulse'
                                cellContent = <span className="chess-val-op-warning" style={{ display: 'inline-flex', alignItems: 'center' }}>{ICONS.warning}</span>
                                cellTitle = `${op.title}: требуется заполнить!`
                              }
                            } else {
                              cellContent = <span className="chess-empty">—</span>
                            }
                          }

                          return (
                            <div
                              key={iso}
                              className={`chess-grid-cell chess-data-cell chess-cell-op ${isToday ? 'chess-cell-today' : ''} ${cellClass}`}
                              style={{
                                '--op-color': op.color,
                                ...(hasData ? { background: `color-mix(in srgb, ${op.color} 8%, transparent)` } : {})
                              }}
                              onClick={() => handleOpCellClick(iso, op)}
                              title={cellTitle}
                            >
                              {cellContent}
                            </div>
                          )
                        })}
                        <div className="chess-grid-cell chess-total-cell" style={valOpTotal > 0 ? { background: `color-mix(in srgb, ${op.color} 8%, transparent)` } : {}}>
                          <span className="chess-total-val" style={{ color: op.color, fontWeight: 800 }}>{formatNumber(valOpTotal)}</span>
                        </div>
                      </div>
                    );
                  })}

                <div className="chess-grid-row chess-employee-row-hours" style={{ gridTemplateColumns: gridCols, background: 'rgba(59, 130, 246, 0.02)', borderBottom: '1px solid var(--surface-border)' }}>
                  <div className="chess-grid-cell chess-name-cell chess-employee-op-name" style={{ color: 'var(--text-secondary)' }}>
                    <span className="chess-op-icon-wrap" style={{ color: 'var(--text-secondary)' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    </span>
                    <span className="chess-op-name">Часы</span>
                  </div>
                  {monthDays.map(d => {
                    const iso = fmtIso(d)
                    const isToday = iso === today
                    const empId = selectedEmployee || profile?.id
                    const hours = getShiftHours(empId, iso)
                    return (
                      <div
                        key={iso}
                        className={`chess-grid-cell chess-data-cell ${isToday ? 'chess-cell-today' : ''}`}
                        style={{ fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default' }}
                      >
                        {hours > 0 ? (
                          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{hours}</span>
                        ) : (
                          <span className="chess-empty">—</span>
                        )}
                      </div>
                    )
                  })}
                  <div className="chess-grid-cell chess-total-cell chess-employee-op-total" style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
                    <span className="chess-total-val-op">
                      {monthDays.reduce((acc, d) => acc + getShiftHours(selectedEmployee || profile?.id, fmtIso(d)), 0)}&nbsp;ч
                    </span>
                  </div>
                </div>

                {canSeeSalary && (
                  <div className="chess-grid-row chess-employee-row-salary" style={{ gridTemplateColumns: gridCols, background: 'rgba(16, 185, 129, 0.03)' }}>
                    <div className="chess-grid-cell chess-name-cell chess-employee-op-name" style={{ color: 'var(--success)', fontWeight: 600 }}>
                      <span className="chess-op-icon-wrap" style={{ color: 'var(--success)' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                      </span>
                      <span className="chess-op-name">Зарплата, ₽</span>
                    </div>
                    {monthDays.map(d => {
                      const iso = fmtIso(d)
                      const isToday = iso === today
                      const empId = selectedEmployee || profile?.id
                      const daySal = salariesData[empId]?.dailyDetails?.[iso]
                      const val = daySal ? Math.round(daySal.total) : 0
                      
                      let cellTitle = 'Нет начислений за этот день'
                      if (daySal && val > 0) {
                        const parts = []
                        if (daySal.basePay > 0) parts.push(`Оклад: ${formatNumber(daySal.basePay)} ₽`)
                        if (daySal.acceptBonus > 0) parts.push(`Приёмка: +${formatNumber(daySal.acceptBonus)} ₽`)
                        if (daySal.issuedBonus > 0) parts.push(`Выдача: +${formatNumber(daySal.issuedBonus)} ₽`)
                        if (daySal.returnBonus > 0) parts.push(`Возвраты: +${formatNumber(daySal.returnBonus)} ₽`)
                        if (daySal.customWorkBonus > 0) parts.push(`Доп. правила: +${formatNumber(daySal.customWorkBonus)} ₽`)
                        if (daySal.bonuses > 0) parts.push(`Премия: +${formatNumber(daySal.bonuses)} ₽`)
                        if (daySal.fines > 0) parts.push(`Штраф: -${formatNumber(daySal.fines)} ₽`)
                        if (daySal.helperDeduction > 0) parts.push(`За помощника: -${formatNumber(daySal.helperDeduction)} ₽`)
                        if (daySal.wbDeductions > 0) parts.push(`Удержание WB: -${formatNumber(daySal.wbDeductions)} ₽`)
                        
                        const empObj = employees.find(e => e.id === empId)
                        const empName = empObj ? (empObj.full_name || empObj.name) : 'Сотрудник'
                        cellTitle = `${empName} за ${new Date(iso).toLocaleDateString('ru')}:\n${parts.join('\n')}\nИтого: ${formatNumber(val)} ₽`
                      }

                      return (
                        <div
                          key={iso}
                          className={`chess-grid-cell chess-data-cell chess-cell-salary ${isToday ? 'chess-cell-today' : ''}`}
                          style={{ fontSize: '11px', cursor: (daySal && val > 0) ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          title={cellTitle}
                          onClick={() => {
                            const empId = selectedEmployee || profile?.id
                            const empObj = employees.find(e => e.id === empId)
                            const empName = empObj ? (empObj.full_name || empObj.name) : 'Сотрудник'
                            if (daySal && val > 0) {
                              handleShowSalaryDetail(empId, empName, iso, daySal)
                            }
                          }}
                        >
                          {val > 0 ? (
                            <span style={{ color: 'var(--success)', fontWeight: 600 }}>{formatNumber(val)}</span>
                          ) : (
                            <span className="chess-empty">—</span>
                          )}
                        </div>
                      )
                    })}
                    <div className="chess-grid-cell chess-total-cell chess-employee-op-total" style={{ color: 'var(--success)', fontWeight: 700 }}>
                      <span className="chess-total-val-op">
                        {salariesData[selectedEmployee || profile?.id] ? formatNumber(salariesData[selectedEmployee || profile?.id].total) : 0}&nbsp;₽
                      </span>
                    </div>
                  </div>
                )}
                {canSeeSalary && (() => {
                  const empId = selectedEmployee || profile?.id
                  const hasBonuses = monthDays.some(d => (salariesData[empId]?.dailyDetails?.[fmtIso(d)]?.bonuses || 0) > 0)
                  const hasFines = monthDays.some(d => (salariesData[empId]?.dailyDetails?.[fmtIso(d)]?.fines || 0) > 0)
                  
                  return (
                    <>
                      {hasBonuses && (
                        <div className="chess-grid-row chess-employee-row-bonus" style={{ gridTemplateColumns: gridCols, animationDelay: '100ms' }}>
                          <div className="chess-grid-cell chess-name-cell chess-employee-op-name" style={{ borderLeft: '4px solid var(--success)', paddingLeft: '24px' }}>
                            <span className="chess-op-icon-wrap" style={{ color: 'var(--success)' }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                            </span>
                            <span className="chess-op-name" style={{ color: 'var(--text-primary)', fontWeight: 500 }}>Премия</span>
                          </div>
                          {monthDays.map(d => {
                            const iso = fmtIso(d)
                            const val = salariesData[empId]?.dailyDetails?.[iso]?.bonuses || 0
                            const isToday = iso === today
                            return (
                              <div key={iso} className={`chess-grid-cell chess-data-cell ${isToday ? 'chess-cell-today' : ''}`} style={{ fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default' }}>
                                {val > 0 ? <span style={{ color: 'var(--success)', fontWeight: 600 }}>+{formatNumber(val)}</span> : <span className="chess-empty">—</span>}
                              </div>
                            )
                          })}
                          <div className="chess-grid-cell chess-total-cell chess-employee-op-total" style={{ fontWeight: 600 }}>
                            <span className="chess-total-val-op" style={{ color: 'var(--success)' }}>
                              {formatNumber(monthDays.reduce((acc, d) => acc + (salariesData[empId]?.dailyDetails?.[fmtIso(d)]?.bonuses || 0), 0))}
                            </span>
                          </div>
                        </div>
                      )}
                      {hasFines && (
                        <div className="chess-grid-row chess-employee-row-fine" style={{ gridTemplateColumns: gridCols, animationDelay: '120ms' }}>
                          <div className="chess-grid-cell chess-name-cell chess-employee-op-name" style={{ borderLeft: '4px solid var(--danger)', paddingLeft: '24px' }}>
                            <span className="chess-op-icon-wrap" style={{ color: 'var(--danger)' }}>{ICONS.warning}</span>
                            <span className="chess-op-name" style={{ color: 'var(--text-primary)', fontWeight: 500 }}>Штраф</span>
                          </div>
                          {monthDays.map(d => {
                            const iso = fmtIso(d)
                            const val = salariesData[empId]?.dailyDetails?.[iso]?.fines || 0
                            const isToday = iso === today
                            const hasFine = val > 0
                            const empObj = employees.find(e => e.id === empId)
                            const empName = empObj ? (empObj.full_name || empObj.name) : 'Сотрудник'
                            return (
                              <div
                                key={iso}
                                className={`chess-grid-cell chess-data-cell ${isToday ? 'chess-cell-today' : ''}`}
                                style={{
                                  fontSize: '11px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: hasFine ? 'pointer' : 'default'
                                }}
                                title={hasFine ? `${empName}: Штрафы за ${new Date(iso).toLocaleDateString('ru-RU')}: -${formatNumber(val)} ₽ (нажмите для деталей)` : 'Штрафов нет'}
                                onClick={() => {
                                  if (hasFine) {
                                    handleShowFineDetail(empId, empName, iso, val)
                                  }
                                }}
                              >
                                {hasFine ? <span style={{ color: 'var(--danger)', fontWeight: 600 }}>-{formatNumber(val)}</span> : <span className="chess-empty">—</span>}
                              </div>
                            )
                          })}
                          <div className="chess-grid-cell chess-total-cell chess-employee-op-total" style={{ fontWeight: 600 }}>
                            <span className="chess-total-val-op" style={{ color: 'var(--danger)' }}>
                              {formatNumber(monthDays.reduce((acc, d) => acc + (salariesData[empId]?.dailyDetails?.[fmtIso(d)]?.fines || 0), 0))}
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )
                })()}
              </>
            )}

              {/* ── Totals Row ── */}
              <div className="chess-grid-row chess-grid-totals-row" style={{ gridTemplateColumns: gridCols }}>
                <div className="chess-grid-cell chess-name-cell chess-name-total">
                  <span className="chess-total-label">Итого</span>
                </div>
                {monthDays.map(d => {
                  const iso = fmtIso(d)
                  const isToday = iso === today
                  return (
                    <div key={iso} className={`chess-grid-cell chess-col-total ${isToday ? 'chess-cell-today' : ''}`}>
                      <span className="chess-col-total-val">{formatNumber(colTotals[iso] || 0)}</span>
                    </div>
                  )
                })}
                <div className="chess-grid-cell chess-grand-total">
                  <span className="chess-grand-total-val">{formatNumber(grandTotal)}</span>
                </div>
              </div>

              {/* ── Salary Totals Row ── */}
              {showEmployeeRows && canSeeSalary && (
                <div className="chess-grid-row" style={{ gridTemplateColumns: gridCols, background: 'rgba(16, 185, 129, 0.05)', borderTop: '2px solid rgba(16, 185, 129, 0.15)', borderBottom: '1px solid var(--surface-border)' }}>
                  <div className="chess-grid-cell chess-name-cell" style={{ background: 'rgba(16, 185, 129, 0.05)', color: 'var(--success)', fontWeight: 700, fontSize: '11px', borderRight: '2px solid var(--surface-border)', gap: '6px' }}>
                    <span className="chess-op-icon-wrap" style={{ color: 'var(--success)' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                    </span>
                    <span className="chess-op-name" style={{ color: 'var(--success)', fontWeight: 700 }}>Итого зарплата, ₽</span>
                  </div>
                  {monthDays.map(d => {
                    const iso = fmtIso(d)
                    const isToday = iso === today
                    const val = salaryDailyTotals[iso] || 0
                    return (
                      <div 
                        key={iso} 
                        className={`chess-grid-cell chess-col-total ${isToday ? 'chess-cell-today' : ''}`}
                        style={{ fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        {val > 0 ? (
                          <span style={{ color: 'var(--success)', fontWeight: 700 }}>{formatNumber(val)}</span>
                        ) : (
                          <span className="chess-empty">—</span>
                        )}
                      </div>
                    )
                  })}
                  <div className="chess-grid-cell chess-total-cell" style={{ background: 'rgba(16, 185, 129, 0.1)', borderLeft: '2px solid var(--surface-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '13px', fontWeight: 800, color: 'var(--success)' }}>{formatNumber(salaryGrandTotal)}&nbsp;₽</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Legend ── */}
      <div className="chess-legend" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', marginTop: '12px', fontSize: '11px', color: 'var(--text-secondary)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'rgba(16, 185, 129, 0.06)', borderBottom: '2px solid var(--success)' }} />
          <span>Смена отработана (есть операции)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {ICONS.warning}
          </span>
          <span style={{ color: 'var(--danger)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>Была смена, но нет операций {ICONS.warning}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'rgba(226, 232, 240, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed var(--surface-border)' }}>
            {ICONS.skip}
          </span>
          <span>Операция пропущена / не требуется</span>
        </div>
      </div>

      {/* ── Cell Action Modal (Skip / Fill / Restore Single) ── */}
      <Modal
        isOpen={!!cellActionModal}
        onClose={() => setCellActionModal(null)}
        title={cellActionModal ? `${cellActionModal.empName} — ${cellActionModal.opTitle}` : ''}
        isBottomSheet={true}
        data-tour="chess-cell-modal"
      >
        {cellActionModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0 16px' }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(203,17,171,0.06), rgba(147,51,234,0.06))',
              border: '1px solid rgba(203,17,171,0.2)',
              borderRadius: '12px', padding: '12px 16px', fontSize: '13px', color: 'var(--text-secondary)'
            }}>
              <div>Смена: <strong>{new Date(cellActionModal.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'short' })}</strong></div>
              <div style={{ marginTop: '4px' }}>Требуемая операция <strong>{cellActionModal.opTitle}</strong> не заполнена.</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button className="btn btn-primary" onClick={triggerAddFromModal} style={{ width: '100%' }}>
                Внести данные операции
              </button>
              
              {cellActionModal.isDismissed ? (
                <button className="btn btn-secondary" onClick={triggerRestoreFromModal} style={{ width: '100%' }}>
                  Восстановить (требовать заполнения)
                </button>
              ) : (
                <button className="btn btn-secondary" style={{ width: '100%', borderColor: 'var(--danger)', color: 'var(--danger)' }} onClick={triggerDismissFromModal}>
                  Пропустить (не требовать заполнения)
                </button>
              )}

              <button className="btn btn-ghost" onClick={() => setCellActionModal(null)} style={{ width: '100%', marginTop: '4px' }}>
                Отмена
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Bulk Action Modal (Skip / Restore Multiple) ── */}
      <Modal
        isOpen={!!bulkActionModal}
        onClose={() => setBulkActionModal(null)}
        title={bulkActionModal ? `${bulkActionModal.empName} — Управление сменой` : ''}
        isBottomSheet={true}
      >
        {bulkActionModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0 16px' }}>
            
            {/* Context message */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(203,17,171,0.06), rgba(147,51,234,0.06))',
              border: '1px solid rgba(203,17,171,0.2)',
              borderRadius: '12px', padding: '12px 16px', fontSize: '13px', color: 'var(--text-secondary)'
            }}>
              <div>Смена: <strong>{new Date(bulkActionModal.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'short' })}</strong></div>
              <div style={{ marginTop: '4px' }}>Отметьте операции, которые хотите <strong>пропустить</strong> (не вносить) на этой смене:</div>
            </div>

            {/* Checkboxes for operations */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 8px' }}>
              {['acceptance', 'issued', 'returns'].map(type => {
                const isMissing = bulkActionModal.missingTypes.includes(type)
                const isDismissed = bulkActionModal.dismissedTypes.includes(type)
                
                const labels = { acceptance: 'Приёмка', issued: 'Выдача', returns: 'Возврат' }
                const isChecked = selectedTypes.includes(type)

                const toggleType = () => {
                  setSelectedTypes(prev => 
                    prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
                  )
                }

                return (
                  <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', cursor: 'pointer', userSelect: 'none' }}>
                    <input 
                      type="checkbox" 
                      checked={isChecked} 
                      onChange={toggleType}
                      style={{ width: '18px', height: '18px', accentColor: 'var(--wb-primary)' }}
                    />
                    <div style={{ display: 'flex', flex: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600 }}>{labels[type]}</span>
                      {isMissing && (
                        <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '6px', background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {ICONS.warning} Не заполнено
                        </span>
                      )}
                      {isDismissed && (
                        <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '6px', background: 'rgba(226, 232, 240, 0.15)', color: 'var(--text-secondary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {ICONS.skip} Пропущено
                        </span>
                      )}
                      {!isMissing && !isDismissed && <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '4px', background: 'rgba(16, 185, 129, 0.1)', color: 'var(--success)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '3px' }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Заполнено</span>}
                    </div>
                  </label>
                )
              })}
            </div>

            {/* Bulk actions */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
              <button 
                className="btn btn-secondary" 
                onClick={handleBulkRestore}
                disabled={selectedTypes.length === 0}
                style={{ flex: 1, fontSize: '13px', padding: '12px 6px' }}
              >
                Восстановить выбранные
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleBulkDismiss}
                disabled={selectedTypes.length === 0}
                style={{ flex: 1, fontSize: '13px', padding: '12px 6px', background: 'var(--wb-primary)', borderColor: 'var(--wb-primary)' }}
              >
                Пропустить выбранные
              </button>
            </div>

            {/* Quick entry links */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid var(--surface-border)', paddingTop: '14px', marginTop: '4px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: 600 }}>ВНЕСТИ ДАННЫЕ ВРУЧНУЮ:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {['acceptance', 'issued', 'returns'].map(type => {
                  const labels = { acceptance: 'Приёмка', issued: 'Выдача', returns: 'Возврат' }
                  return (
                    <button 
                      key={type} 
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        onAddRecord?.(bulkActionModal.date, bulkActionModal.empId, type)
                        setBulkActionModal(null)
                      }}
                      style={{ fontSize: '12px', padding: '8px 12px' }}
                    >
                      ＋ {labels[type]}
                    </button>
                  )
                })}
              </div>
            </div>

            <button className="btn btn-ghost" onClick={() => setBulkActionModal(null)} style={{ width: '100%', marginTop: '6px' }}>
              Отмена
            </button>

          </div>
        )}
      </Modal>

      {/* ── Floating Skip Action Bar ── */}
      {isSelectMode && createPortal(
        <div style={{
          position: 'fixed',
          bottom: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--surface)',
          border: '1px solid rgba(203,17,171,0.25)',
          boxShadow: '0 12px 30px rgba(0,0,0,0.2)',
          borderRadius: '16px',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          zIndex: 10000,
          animation: 'slideUp 0.3s ease',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}>
          {selectedCells.length > 0 ? (
            <span style={{ fontSize: '13px', fontWeight: 600 }}>
              Выбрано операций: <strong style={{ color: 'var(--wb-primary)' }}>{selectedCells.length}</strong>
            </span>
          ) : (
            <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>
              Выберите ячейки в таблице для пропуска
            </span>
          )}
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={handleBulkRestoreSelected} 
            disabled={selectedCells.length === 0}
            style={{ fontSize: '12px', borderRadius: '10px' }}
          >
            Восстановить
          </button>
          <button 
            className="btn btn-primary btn-sm" 
            onClick={handleBulkDismissSelected} 
            disabled={selectedCells.length === 0}
            style={{ 
              background: 'var(--wb-primary)', 
              borderColor: 'var(--wb-primary)', 
              fontSize: '12px',
              borderRadius: '10px',
              opacity: selectedCells.length === 0 ? 0.6 : 1
            }}
          >
            Пропустить выбранные
          </button>
          <button 
            className="btn btn-ghost btn-sm" 
            onClick={() => { 
              if (selectedCells.length > 0) {
                setSelectedCells([]) 
              } else {
                setIsSelectMode(false)
              }
            }} 
            style={{ fontSize: '12px', color: 'var(--text-secondary)' }}
          >
            {selectedCells.length > 0 ? 'Сбросить' : 'Выйти'}
          </button>
        </div>,
        document.body
      )}

      {/* ── Records List Modal ── */}
      <Modal
        isOpen={!!recordsListModal}
        onClose={() => setRecordsListModal(null)}
        title={recordsListModal ? `${recordsListModal.empName} — ${recordsListModal.opTitle}` : ''}
        isBottomSheet={true}
      >
        {recordsListModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0 16px' }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.06), rgba(147, 51, 234, 0.06))',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '12px', padding: '12px 16px', fontSize: '13px', color: 'var(--text-secondary)'
            }}>
              <div>Дата: <strong>{new Date(recordsListModal.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'short' })}</strong></div>
              <div style={{ marginTop: '4px' }}>Найдено несколько записей за этот день. Выберите запись для редактирования или добавьте новую.</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '300px', overflowY: 'auto', paddingRight: '4px' }}>
              {recordsListModal.records.map((exp) => (
                <div 
                  key={exp.id} 
                  style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '6px', 
                    padding: '12px', 
                    background: 'var(--surface-color)', 
                    border: '1px solid var(--border-color)', 
                    borderRadius: '12px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '16px', fontWeight: 700, color: exp.type === 'expense_var' ? 'var(--danger)' : 'var(--text-primary)' }}>
                      {formatNumber(exp.count || exp.amount || 0)} {exp.type === 'expense_var' ? '₽' : ''}
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          onEditRecord?.(exp)
                          setRecordsListModal(null)
                        }}
                        style={{ padding: '4px 12px', fontSize: '12px', borderRadius: '8px' }}
                      >
                        Редактировать
                      </button>
                      {(isAdmin || userPermissions?.canManageOperations || (exp.created_at && new Date(exp.created_at).toDateString() === new Date().toDateString())) && (
                        <button 
                          className="btn btn-secondary btn-sm text-danger"
                          onClick={() => setItemToDelete(exp)}
                          style={{ padding: '4px 12px', fontSize: '12px', borderRadius: '8px', color: 'var(--danger)', borderColor: 'var(--danger)' }}
                        >
                          Удалить
                        </button>
                      )}
                    </div>
                  </div>
                  {exp.comment && (
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', background: 'var(--background-color)', padding: '6px 10px', borderRadius: '8px', borderLeft: '3px solid var(--border-color)' }}>
                      {exp.comment}
                    </div>
                  )}
                  {exp.box_number && (
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                      Категория/Коробка: {exp.box_number}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  onAddRecord?.(recordsListModal.date, recordsListModal.empId, recordsListModal.opId)
                  setRecordsListModal(null)
                }}
                style={{ width: '100%', background: 'var(--wb-primary)', borderColor: 'var(--wb-primary)' }}
              >
                ＋ Добавить новую запись
              </button>
              
              <button className="btn btn-ghost" onClick={() => setRecordsListModal(null)} style={{ width: '100%' }}>
                Закрыть
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Confirmation Delete Modal ── */}
      <Modal 
        isOpen={!!itemToDelete} 
        onClose={() => setItemToDelete(null)} 
        title="Удаление записи" 
        isBottomSheet={true}
      >
        <div style={{ padding: '10px 0 24px 0', color: 'var(--text-secondary)' }}>
          Вы уверены, что хотите удалить эту операцию? Это действие нельзя отменить.
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button type="button" className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setItemToDelete(null)}>
            Отмена
          </button>
          <button type="button" className="btn btn-primary" style={{ flex: 1, background: 'var(--danger)' }} onClick={handleConfirmDelete}>
            Удалить
          </button>
        </div>
      </Modal>

      {/* ── Salary Day Detail Modal ── */}
      <Modal
        isOpen={!!salaryDetailModal}
        onClose={() => setSalaryDetailModal(null)}
        title={salaryDetailModal ? `${salaryDetailModal.employeeName}` : ''}
        isBottomSheet={true}
      >
        {salaryDetailModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0 16px' }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(16,185,129,0.06), rgba(59,130,246,0.06))',
              border: '1px solid rgba(16,185,129,0.2)',
              borderRadius: '12px', padding: '12px 16px', fontSize: '13px', color: 'var(--text-secondary)'
            }}>
              <div>Дата: <strong>{salaryDetailModal.dateFormatted}</strong></div>
              <div style={{ marginTop: '4px' }}>Детализация начислений за смену:</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {salaryDetailModal.details.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--surface-color)', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '13px' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{item.label}</span>
                  <span style={{ fontWeight: 600, color: item.isNegative ? 'var(--danger)' : 'var(--success)' }}>
                    {item.isNegative ? '−' : '+'}{Math.abs(item.value)} ₽
                  </span>
                </div>
              ))}
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'var(--surface-color)', borderTop: '2px solid var(--success)', borderLeft: '1px solid var(--border-color)', borderRight: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)', borderRadius: '0 0 8px 8px', fontSize: '15px', fontWeight: 'bold', marginTop: '6px' }}>
                <span>ИТОГО ЗА ДЕНЬ:</span>
                <span style={{ color: 'var(--success)' }}>{salaryDetailModal.total} ₽</span>
              </div>
            </div>

            <button className="btn btn-ghost" onClick={() => setSalaryDetailModal(null)} style={{ width: '100%', marginTop: '8px' }}>
              Закрыть
            </button>
          </div>
        )}
      </Modal>

      {/* ── Fine Details Modal ── */}
      <Modal
        isOpen={!!finesModal}
        onClose={() => setFinesModal(null)}
        title={finesModal ? `${finesModal.empName} — Штрафы` : ''}
        isBottomSheet={true}
      >
        {finesModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0 16px' }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.08), rgba(245, 158, 11, 0.08))',
              border: '1px solid rgba(239, 68, 68, 0.25)',
              borderRadius: '12px', padding: '12px 16px', fontSize: '13px', color: 'var(--text-secondary)'
            }}>
              <div>Дата: <strong>{new Date(finesModal.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'short' })}</strong></div>
              <div style={{ marginTop: '4px', fontWeight: 600, color: 'var(--danger)' }}>
                Итого штрафов за день: -{formatNumber(finesModal.totalAmount)} ₽
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '360px', overflowY: 'auto', paddingRight: '4px' }}>
              {finesModal.items.map((item, idx) => {
                const creator = employees.find(e => e.id === item.created_by) || (item.created_by === profile?.id ? profile : null)
                const creatorName = creator ? (creator.full_name || creator.name) : ''
                const timeStr = item.created_at ? new Date(item.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : ''
                const amountNum = Math.abs(parseFloat(item.amount) || 0)

                return (
                  <div key={item.id || idx} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    padding: '14px',
                    background: 'var(--surface-color)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '16px', fontWeight: 800, color: 'var(--danger)' }}>
                        -{formatNumber(amountNum)} ₽
                      </span>
                      {timeStr && (
                        <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: 500 }}>
                          Время: {timeStr}
                        </span>
                      )}
                    </div>

                    <div style={{ fontSize: '13px', color: 'var(--text-primary)', background: 'var(--background-color)', padding: '8px 12px', borderRadius: '8px', borderLeft: '3px solid var(--danger)' }}>
                      <strong>Причина:</strong> {item.comment || item.type || 'Штраф / Удержание'}
                    </div>

                    {creatorName && (
                      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>Кто создал:</span> <strong style={{ color: 'var(--text-secondary)' }}>{creatorName}</strong>
                      </div>
                    )}

                    {(isAdmin || userPermissions?.canManageOperations || isOwner) && item.id !== 'synthetic_fine' && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px', paddingTop: '8px', borderTop: '1px dotted var(--border-color)' }}>
                        <button
                          className="btn btn-secondary btn-sm text-danger"
                          style={{ padding: '4px 12px', fontSize: '12px', borderRadius: '8px', color: 'var(--danger)', borderColor: 'var(--danger)' }}
                          onClick={() => handleDeleteFineItem(item)}
                        >
                          Удалить
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'var(--surface-color)', borderTop: '2px solid var(--danger)', borderLeft: '1px solid var(--border-color)', borderRight: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)', borderRadius: '0 0 8px 8px', fontSize: '15px', fontWeight: 'bold', marginTop: '6px' }}>
              <span>ИТОГО ЗА ДЕНЬ:</span>
              <span style={{ color: 'var(--danger)' }}>-{formatNumber(finesModal.totalAmount)} ₽</span>
            </div>

            <button className="btn btn-ghost" onClick={() => setFinesModal(null)} style={{ width: '100%', marginTop: '8px' }}>
              Закрыть
            </button>
          </div>
        )}
      </Modal>

    </div>
  )
}
